/**
 * Spotify embed URL helpers.
 *
 * Strips share tracking (`?si=...`) and converts any open.spotify.com link
 * (playlist | track | album | episode) into a clean embed iframe URL with
 * Apex brand theme params.
 */

const SPOTIFY_URL_RE =
  /https?:\/\/open\.spotify\.com\/(?:embed\/)?(playlist|track|album|episode)\/([A-Za-z0-9]{16,32})/;

export function parseSpotifyUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const m = url.trim().match(SPOTIFY_URL_RE);
  if (!m) return null;
  return { kind: m[1], id: m[2] };
}

/**
 * Build a Spotify embed URL for an iframe.
 * @param {string|object} input  Either a raw Spotify URL or an object
 *   `{ spotify_kind, spotify_id }` (the shape the backend returns).
 * @param {object} [opts]
 * @param {'compact'|'full'} [opts.size='full']  compact=no artwork strip in mini-player
 * @param {0|1} [opts.theme=0]  0 = dark (Spotify default), 1 = white
 * @returns {string|null}
 */
export function buildSpotifyEmbedUrl(input, opts = {}) {
  let kind;
  let id;
  if (typeof input === 'string') {
    const parsed = parseSpotifyUrl(input);
    if (!parsed) return null;
    kind = parsed.kind;
    id = parsed.id;
  } else if (input && typeof input === 'object') {
    kind = input.spotify_kind || input.kind;
    id = input.spotify_id || input.id;
  }
  if (!kind || !id) return null;
  const theme = opts.theme === 1 ? 1 : 0;
  return `https://open.spotify.com/embed/${kind}/${id}?utm_source=generator&theme=${theme}`;
}

export const PLAYLIST_CATEGORIES = [
  { id: 'pregame', label: 'Pre-Game Lock-In', tagline: 'Walk-out energy. Compete-mode music.' },
  { id: 'zone', label: 'In The Zone', tagline: 'Focus + flow. Heads-down training fuel.' },
  { id: 'recovery', label: 'Recovery & Reset', tagline: 'Post-game decompression and breath work.' },
  { id: 'winddown', label: 'Wind Down', tagline: 'Low-BPM cool-out. Sleep prep and between-session calm.' },
  { id: 'focus', label: 'Deep Focus', tagline: 'Film study and homework lock-in.' },
];

export function categoryMeta(id) {
  return PLAYLIST_CATEGORIES.find((c) => c.id === id) || PLAYLIST_CATEGORIES[0];
}

/**
 * Freemium gate (Phase 20A) — categories that free users may play inside
 * the Locker Room hub and the floating mini-player.
 *
 * Decision rationale:
 *   - `pregame`  → highest intent moment (locker room before tip-off).
 *   - `winddown` → opposite end of the arousal curve; covers post-loss reset
 *                  and sleep prep. Together they showcase the full Apex
 *                  emotional range without giving the catalog away.
 *
 * Locked categories (`zone`, `recovery`, `focus`) require the
 * **Locker Room Pass** ($29.99/mo or $299/yr).
 *
 * Note: `/playlists` (the public browse-all page) intentionally bypasses
 * this gate — users can sample any playlist there with full embeds. The
 * gate exists only inside the Locker Room product surfaces.
 */
export const FREE_PLAYLIST_CATEGORIES = ['pregame', 'winddown'];

export function isFreePlaylist(playlist) {
  if (!playlist || !playlist.category) return false;
  return FREE_PLAYLIST_CATEGORIES.includes(playlist.category);
}
